import { useState } from 'react';
import { Loader2 } from 'lucide-react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '../ui/dialog';
import { deleteFamilyAccount, signOutAfterDeletion, SUPPORT_EMAIL } from '../../lib/accountDeletion';

const FIRST_WARNING = '⚠️ ATTENZIONE: Questa azione è irreversibile!\n\nTutti i tuoi dati, inclusi:\n- Profilo famiglia\n- Storico attività\n- Medicine configurate\n- Dati assistiti\n\nVerranno eliminati permanentemente.\n\nSei sicuro di voler procedere?';
const CONFIRM_WORD = 'OK';

const FAILURE_TEXT = {
  nothing_deleted: 'Eliminazione non riuscita. Nessun dato è stato eliminato. Puoi riprovare.',
  incomplete: 'Eliminazione incompleta: una parte dei tuoi dati è stata eliminata, ma non tutti. Riprova per completare l\'eliminazione.',
  unknown: 'Impossibile contattare il server: non sappiamo se l\'eliminazione è avvenuta. Ricarica la pagina e controlla prima di riprovare.',
  login_not_removed: `I tuoi dati sono stati eliminati, ma non è stato possibile chiudere il tuo accesso. Scrivi a ${SUPPORT_EMAIL} indicando l'email con cui accedi e lo chiuderemo noi.`
};

export const DeleteAccountButton = ({ famiglia }) => {
  const [open, setOpen] = useState(false);
  const [typed, setTyped] = useState('');
  const [busy, setBusy] = useState(false);
  const [failure, setFailure] = useState(null);

  const start = () => {
    if (!window.confirm(FIRST_WARNING)) return;
    setTyped('');
    setFailure(null);
    setOpen(true);
  };

  const confirmDeletion = async () => {
    setBusy(true);
    const outcome = await deleteFamilyAccount(famiglia.id);
    if (outcome === 'deleted') {
      await signOutAfterDeletion();
      window.location.replace('/account-eliminato');
      return;
    }
    setBusy(false);
    setFailure(outcome);
  };

  const finished = failure === 'login_not_removed';

  return (
    <>
      <Button
        variant="outline"
        size="sm"
        onClick={start}
        className="rounded-xl border-red-300 text-red-600 hover:bg-red-50"
        data-testid="delete-account-button"
      >
        Elimina
      </Button>
      <Dialog open={open} onOpenChange={(value) => { if (!busy && !finished) setOpen(value); }}>
        <DialogContent data-testid="delete-account-dialog">
          <DialogHeader>
            <DialogTitle>Elimina Account</DialogTitle>
            <DialogDescription>
              Ultima conferma: digita {CONFIRM_WORD} per eliminare definitivamente il tuo account.
            </DialogDescription>
          </DialogHeader>
          {!finished && (
            <Input
              value={typed}
              onChange={(e) => setTyped(e.target.value)}
              autoFocus
              disabled={busy}
              data-testid="delete-account-confirm-input"
            />
          )}
          {failure && (
            <p className="text-sm text-red-600" role="alert" data-testid={`delete-account-error-${failure}`}>
              {FAILURE_TEXT[failure]}
            </p>
          )}
          <DialogFooter>
            {finished ? (
              <Button onClick={async () => { await signOutAfterDeletion(); window.location.replace('/'); }} data-testid="delete-account-close">
                Chiudi
              </Button>
            ) : (
              <>
                <Button variant="outline" onClick={() => setOpen(false)} disabled={busy} data-testid="delete-account-cancel">
                  Annulla
                </Button>
                <Button
                  onClick={confirmDeletion}
                  disabled={busy || typed.trim().toUpperCase() !== CONFIRM_WORD}
                  className="bg-red-600 hover:bg-red-700 text-white"
                  data-testid="delete-account-confirm"
                >
                  {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Elimina definitivamente'}
                </Button>
              </>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
};
